import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import axiosInstance from '@/lib/axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, QrCode, Trash2, Edit, ChevronLeft, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EventsPageShell } from '../components/EventsPageShell';
import { ActivityScanSettingsEditor } from '@/components/events/ActivityScanSettingsEditor';
import {
  type ActivityScanField,
  type DuplicateScanBehavior,
  parseActivityScanFields,
} from '@/lib/activity-scan-fields';

interface EventActivity {
  id: string;
  name: string;
  description: string | null;
  allowWalkIns: boolean;
  walkInTicketId: string | null;
  allowDuplicateScans?: boolean;
  duplicateScanBehavior?: DuplicateScanBehavior;
  scanEntryFields?: ActivityScanField[];
  allowSelfMarking?: boolean;
  selfMarkRequiresConfirmation?: boolean;
  selfMarkButtonLabel?: string;
  scanCount: number;
  createdAt: string;
}

export default function EventActivitiesPage() {
  const { communityId, eventId } = useParams();
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<EventActivity | null>(null);
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [allowWalkIns, setAllowWalkIns] = useState(false);
  const [walkInTicketId, setWalkInTicketId] = useState<string>('none');
  const [allowDuplicateScans, setAllowDuplicateScans] = useState(false);
  const [duplicateScanBehavior, setDuplicateScanBehavior] =
    useState<DuplicateScanBehavior>('CREATE_NEW');
  const [scanEntryFields, setScanEntryFields] = useState<ActivityScanField[]>([]);
  const [allowSelfMarking, setAllowSelfMarking] = useState(false);
  const [selfMarkRequiresConfirmation, setSelfMarkRequiresConfirmation] = useState(true);
  const [selfMarkButtonLabel, setSelfMarkButtonLabel] = useState("I'm here");
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setItemName('');
    setItemDescription('');
    setAllowWalkIns(false);
    setWalkInTicketId('none');
    setAllowDuplicateScans(false);
    setDuplicateScanBehavior('CREATE_NEW');
    setScanEntryFields([]);
    setAllowSelfMarking(false);
    setSelfMarkRequiresConfirmation(true);
    setSelfMarkButtonLabel("I'm here");
  };

  const { data: activities, error } = useSWR<EventActivity[]>(
    eventId ? `/api/events/${eventId}/activities` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const { data: tickets } = useSWR<{ id: string; name: string }[]>(
    eventId ? `/api/events/${eventId}/tickets` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axiosInstance.post(`/api/events/${eventId}/activities`, {
        name: itemName,
        description: itemDescription,
        allowWalkIns,
        walkInTicketId: walkInTicketId === 'none' ? null : walkInTicketId,
        allowDuplicateScans,
        duplicateScanBehavior,
        scanEntryFields,
        allowSelfMarking,
        selfMarkRequiresConfirmation,
        selfMarkButtonLabel,
      });
      toast.success('Activity created successfully');
      setIsCreateOpen(false);
      resetForm();
      mutate(`/api/events/${eventId}/activities`);
    } catch (err) {
      toast.error('Failed to create activity');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedActivity) return;
    setIsLoading(true);
    try {
      await axiosInstance.put(`/api/events/${eventId}/activities/${selectedActivity.id}`, {
        name: itemName,
        description: itemDescription,
        allowWalkIns,
        walkInTicketId: walkInTicketId === 'none' ? null : walkInTicketId,
        allowDuplicateScans,
        duplicateScanBehavior,
        scanEntryFields,
        allowSelfMarking,
        selfMarkRequiresConfirmation,
        selfMarkButtonLabel,
      });
      toast.success('Activity updated successfully');
      setIsEditOpen(false);
      setSelectedActivity(null);
      mutate(`/api/events/${eventId}/activities`);
    } catch (err) {
      toast.error('Failed to update activity');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedActivity) return;
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/api/events/${eventId}/activities/${selectedActivity.id}`);
      toast.success('Activity deleted successfully');
      setIsDeleteOpen(false);
      setSelectedActivity(null);
      mutate(`/api/events/${eventId}/activities`);
    } catch (err) {
      toast.error('Failed to delete activity');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (activity: EventActivity) => {
    setSelectedActivity(activity);
    setItemName(activity.name);
    setItemDescription(activity.description || '');
    setAllowWalkIns(activity.allowWalkIns || false);
    setWalkInTicketId(activity.walkInTicketId || 'none');
    setAllowDuplicateScans(activity.allowDuplicateScans ?? false);
    setDuplicateScanBehavior(
      activity.duplicateScanBehavior === 'UPDATE_EXISTING' ? 'UPDATE_EXISTING' : 'CREATE_NEW'
    );
    setScanEntryFields(parseActivityScanFields(activity.scanEntryFields));
    setAllowSelfMarking(activity.allowSelfMarking ?? false);
    setSelfMarkRequiresConfirmation(activity.selfMarkRequiresConfirmation ?? true);
    setSelfMarkButtonLabel(activity.selfMarkButtonLabel || "I'm here");
    setIsEditOpen(true);
  };

  const openDelete = (activity: EventActivity) => {
    setSelectedActivity(activity);
    setIsDeleteOpen(true);
  };

  return (
    <EventsPageShell maxWidthClassName="max-w-7xl">
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <Link
              to={`/${communityId}/events/${eventId}/manage`}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center mb-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold">Event Activities</h1>
            <p className="text-muted-foreground">
              Manage check-in points, swag distribution, and other activities.
            </p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Create Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreate}>
                <DialogHeader>
                  <DialogTitle>Create New Activity</DialogTitle>
                  <DialogDescription>
                    Add a new measurable activity to your event.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={itemName}
                      onChange={(e) => setItemName(e.target.value)}
                      placeholder="e.g. Morning Check-in, Lunch, Swag"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      value={itemDescription}
                      onChange={(e) => setItemDescription(e.target.value)}
                      placeholder="Brief details about this activity"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      id="allow-walkins"
                      checked={allowWalkIns}
                      onCheckedChange={setAllowWalkIns}
                    />
                    <Label htmlFor="allow-walkins" className="flex flex-col gap-1">
                      <span>Allow Profile Walk-ins</span>
                      <span className="font-normal text-xs text-muted-foreground">
                        Auto register users showing their Profile QR.
                      </span>
                    </Label>
                  </div>
                  {allowWalkIns && (
                    <div className="space-y-2">
                      <Label htmlFor="walkin-ticket">Default Ticket</Label>
                      <Select value={walkInTicketId} onValueChange={setWalkInTicketId}>
                        <SelectTrigger id="walkin-ticket">
                          <SelectValue placeholder="Select a ticket" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {tickets?.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      id="allow-self-marking"
                      checked={allowSelfMarking}
                      onCheckedChange={setAllowSelfMarking}
                    />
                    <Label htmlFor="allow-self-marking" className="flex flex-col gap-1">
                      <span>Allow self marking</span>
                      <span className="font-normal text-xs text-muted-foreground">
                        Attendees can mark themselves present from their phone.
                      </span>
                    </Label>
                  </div>
                  {allowSelfMarking && (
                    <>
                      <div className="flex items-center space-x-2 pt-2">
                        <Switch
                          id="self-mark-confirm"
                          checked={selfMarkRequiresConfirmation}
                          onCheckedChange={setSelfMarkRequiresConfirmation}
                        />
                        <Label htmlFor="self-mark-confirm" className="flex flex-col gap-1">
                          <span>Require scanner confirmation</span>
                          <span className="font-normal text-xs text-muted-foreground">
                            Manager must confirm each self-mark on the activity scanner.
                          </span>
                        </Label>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="self-mark-label">Self-mark button label</Label>
                        <Input
                          id="self-mark-label"
                          value={selfMarkButtonLabel}
                          onChange={(e) => setSelfMarkButtonLabel(e.target.value)}
                          placeholder="I'm here"
                          maxLength={100}
                          required
                        />
                      </div>
                    </>
                  )}
                  <ActivityScanSettingsEditor
                    allowDuplicateScans={allowDuplicateScans}
                    onAllowDuplicateScansChange={setAllowDuplicateScans}
                    duplicateScanBehavior={duplicateScanBehavior}
                    onDuplicateScanBehaviorChange={setDuplicateScanBehavior}
                    scanEntryFields={scanEntryFields}
                    onScanEntryFieldsChange={setScanEntryFields}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Activity'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {error && (
          <div className="p-8 text-center text-destructive bg-destructive/10 rounded-lg">
            Error loading activities: {error.message}
          </div>
        )}

        {!activities && !error && <div className="p-8 text-center">Loading activities...</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activities?.map((activity) => (
            <Card key={activity.id} className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex justify-between items-start">
                  <span>{activity.name}</span>
                </CardTitle>
                <CardDescription>{activity.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black tracking-tighter text-primary">
                    {activity.scanCount}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Total Scans
                  </span>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col items-stretch border-t bg-muted/10 pt-4 gap-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(activity)}
                      className="h-8 w-8"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive h-8 w-8"
                      onClick={() => openDelete(activity)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {activity.allowSelfMarking ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => {
                        const url = `${window.location.origin}/${communityId}/events/${eventId}/activities/${activity.id}/self-mark`;
                        void navigator.clipboard.writeText(url);
                        toast.success('Self-mark link copied');
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-1.5" />
                      Copy link
                    </Button>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  className="w-full shadow-sm"
                  onClick={() =>
                    navigate(`/${communityId}/events/${eventId}/activities/${activity.id}/scanner`)
                  }
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Launch Scanner
                </Button>
              </CardFooter>
            </Card>
          ))}
          {activities?.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground bg-muted rounded-lg border border-dashed">
              No activities found. Create one to get started.
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleUpdate}>
              <DialogHeader>
                <DialogTitle>Edit Activity</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="edit-allow-walkins"
                    checked={allowWalkIns}
                    onCheckedChange={setAllowWalkIns}
                  />
                  <Label htmlFor="edit-allow-walkins" className="flex flex-col gap-1">
                    <span>Allow Profile Walk-ins</span>
                    <span className="font-normal text-xs text-muted-foreground">
                      Auto register users showing their Profile QR.
                    </span>
                  </Label>
                </div>
                {allowWalkIns && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-walkin-ticket">Default Ticket</Label>
                    <Select value={walkInTicketId} onValueChange={setWalkInTicketId}>
                      <SelectTrigger id="edit-walkin-ticket">
                        <SelectValue placeholder="Select a ticket" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {tickets?.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="edit-allow-self-marking"
                    checked={allowSelfMarking}
                    onCheckedChange={setAllowSelfMarking}
                  />
                  <Label htmlFor="edit-allow-self-marking" className="flex flex-col gap-1">
                    <span>Allow self marking</span>
                    <span className="font-normal text-xs text-muted-foreground">
                      Attendees can mark themselves present from their phone.
                    </span>
                  </Label>
                </div>
                {allowSelfMarking && (
                  <>
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="edit-self-mark-confirm"
                        checked={selfMarkRequiresConfirmation}
                        onCheckedChange={setSelfMarkRequiresConfirmation}
                      />
                      <Label htmlFor="edit-self-mark-confirm" className="flex flex-col gap-1">
                        <span>Require scanner confirmation</span>
                        <span className="font-normal text-xs text-muted-foreground">
                          Manager must confirm each self-mark on the activity scanner.
                        </span>
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-self-mark-label">Self-mark button label</Label>
                      <Input
                        id="edit-self-mark-label"
                        value={selfMarkButtonLabel}
                        onChange={(e) => setSelfMarkButtonLabel(e.target.value)}
                        placeholder="I'm here"
                        maxLength={100}
                        required
                      />
                    </div>
                  </>
                )}
                <ActivityScanSettingsEditor
                  allowDuplicateScans={allowDuplicateScans}
                  onAllowDuplicateScansChange={setAllowDuplicateScans}
                  duplicateScanBehavior={duplicateScanBehavior}
                  onDuplicateScanBehaviorChange={setDuplicateScanBehavior}
                  scanEntryFields={scanEntryFields}
                  onScanEntryFieldsChange={setScanEntryFields}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Activity?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedActivity?.name}"? Tihs will also delete
                all scan logs associated with this activity. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </EventsPageShell>
  );
}
